import { useId, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import {
  Button,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  ProgressBar,
} from '@ludus/ui'
import type { AuditsApiError } from '../../api/index.ts'
import { useExtendQuota } from '../../hooks/index.ts'
import type { AuditSpend, AuditState } from '../../types.ts'
import { formatAmount, quotaPercent } from '../../utils/format.ts'

interface QuotaGaugeProps {
  auditId: string
  state: AuditState
  spend: AuditSpend
  onChanged: () => Promise<void>
}

const extendSchema = z.object({
  amount: z.coerce.number().min(0, 'nonNegative'),
  asrMinutes: z.coerce.number().min(0, 'nonNegative'),
  llmTokens: z.coerce.number().min(0, 'nonNegative'),
})

type ExtendValues = z.infer<typeof extendSchema>

/** Spent against the quota, and Extend — the `PATCH …/quota` dialog a paused audit resumes through (§8.3). */
export function QuotaGauge({ auditId, state, spend, onChanged }: QuotaGaugeProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const percent = quotaPercent(spend.spent.amount, spend.quota)
  const paused = state === 'paused_quota'
  return (
    <div className="flex min-w-[16rem] flex-col gap-1">
      <div className="flex items-center justify-between text-xs">
        <span className="font-medium text-foreground">
          {t('pages.audits.quota.title', { defaultValue: 'Quota' })}
        </span>
        <span className="text-muted">
          {formatAmount(spend.spent.amount, spend.quota.currency)} /{' '}
          {formatAmount(spend.quota.amount, spend.quota.currency)}
        </span>
      </div>
      <ProgressBar
        value={percent}
        tone={paused || spend.exhausted !== null ? 'danger' : percent >= 80 ? 'warning' : 'primary'}
        label={t('pages.audits.quota.gauge', { defaultValue: 'Spent against the quota' })}
      />
      <div className="flex items-center justify-between text-[11px] text-muted">
        <span>
          {t('pages.audits.quota.perUnit', {
            defaultValue: '{{units}} units · {{perUnit}} per unit',
            units: spend.units,
            perUnit: formatAmount(spend.perUnit, spend.quota.currency),
          })}
        </span>
        <Button size="sm" variant={paused ? 'primary' : 'ghost'} onClick={() => setOpen(true)}>
          {t('pages.audits.quota.extend', { defaultValue: 'Extend' })}
        </Button>
      </div>
      {paused && (
        <p role="status" className="text-[11px] text-toast-warning-text">
          {t('pages.audits.quota.paused', {
            defaultValue: 'The quota is spent ({{axis}}); the worker resumes once it is widened.',
            axis: spend.exhausted ?? 'amount',
          })}
        </p>
      )}
      <ExtendQuotaDialog
        auditId={auditId}
        spend={spend}
        open={open}
        onOpenChange={setOpen}
        onChanged={onChanged}
      />
    </div>
  )
}

function ExtendQuotaDialog({
  auditId,
  spend,
  open,
  onOpenChange,
  onChanged,
}: {
  auditId: string
  spend: AuditSpend
  open: boolean
  onOpenChange: (next: boolean) => void
  onChanged: () => Promise<void>
}) {
  const { t } = useTranslation()
  const formId = useId()
  const { trigger, isMutating } = useExtendQuota(auditId)
  const [failure, setFailure] = useState<string | null>(null)
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ExtendValues>({
    resolver: zodResolver(extendSchema),
    values: {
      amount: spend.quota.amount,
      asrMinutes: spend.quota.asr_minutes,
      llmTokens: spend.quota.llm_tokens,
    },
  })
  const onSubmit = handleSubmit(async (values) => {
    if (isMutating) return
    setFailure(null)
    try {
      await trigger({
        amount: values.amount,
        asr_minutes: values.asrMinutes,
        llm_tokens: values.llmTokens,
      })
      onOpenChange(false)
      await onChanged()
    } catch (err) {
      setFailure((err as AuditsApiError).message)
    }
  })
  const fieldError = (key: keyof ExtendValues) =>
    errors[key]?.message
      ? t(`pages.audits.wizard.errors.${errors[key]?.message}`, { defaultValue: 'invalid' })
      : null
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('pages.audits.quota.dialog.title', { defaultValue: 'Extend the quota' })}
          </DialogTitle>
          <DialogDescription>
            {t('pages.audits.quota.dialog.description', {
              defaultValue:
                'The file, the row and the log move together; a paused audit resumes on the next run.',
            })}
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <form id={formId} onSubmit={onSubmit} className="flex flex-col gap-3 text-sm">
            <label className="flex flex-col gap-1">
              <span className="text-xs text-muted">
                {t('pages.audits.quota.dialog.amount', {
                  defaultValue: 'Amount (minor units, {{currency}})',
                  currency: spend.quota.currency,
                })}
              </span>
              <Input type="number" min={0} {...register('amount')} />
              {fieldError('amount') && (
                <span className="text-[11px] text-toast-error-text">{fieldError('amount')}</span>
              )}
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs text-muted">
                {t('pages.audits.wizard.fields.asrMinutes', { defaultValue: 'ASR minutes' })}
              </span>
              <Input type="number" min={0} {...register('asrMinutes')} />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs text-muted">
                {t('pages.audits.wizard.fields.llmTokens', { defaultValue: 'LLM tokens' })}
              </span>
              <Input type="number" min={0} {...register('llmTokens')} />
            </label>
            {failure && (
              <p role="alert" className="text-xs text-toast-error-text">
                {failure}
              </p>
            )}
          </form>
        </DialogBody>
        <DialogFooter>
          <DialogClose>
            <Button variant="ghost" size="sm" type="button">
              {t('pages.audits.wizard.cancel', { defaultValue: 'Cancel' })}
            </Button>
          </DialogClose>
          <Button variant="primary" size="sm" type="submit" form={formId} disabled={isMutating}>
            {t('pages.audits.quota.dialog.submit', { defaultValue: 'Widen' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
